<template>
  <div class="flex items-center justify-center gap-3 lg:gap-4">
    <template v-for="(digit, index) in length" :key="index">
      <input
        ref="inputRefs"
        v-model="otpValues[index]"
        type="text"
        inputmode="numeric"
        pattern="\d*"
        :id="`otp-${index}`"
        :maxlength="1"
        :disabled="disable || readonly"
        :class="[
          // Base Pebble Input Styles
          'w-12 h-14 lg:w-14 lg:h-16 text-center text-2xl lg:text-3xl font-bold rounded-2xl transition-all outline-none border',
          // Theme & State Styles
          !disable && !readonly
            ? 'bg-white/5 border-white/5 text-brand placeholder:text-text-weak-dark/20 hover:border-white/10 focus:border-brand/50 focus:ring-4 focus:ring-brand/10'
            : 'bg-white/2 border-white/5 text-text-weak-dark/50 cursor-not-allowed opacity-50',
          // Error State
          errors[index] ? 'border-red-500/50 bg-red-500/5' : '',
          inputClasses
        ]"
        @input="handleInput(index)"
        @keydown="handleKeyDown($event, index)"
        @paste="handlePaste"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';

const props = defineProps({
  length: {
    type: Number,
    default: 4,
  },
  modelValue: {
    type: String,
    default: '',
  },
  inputClasses: {
    type: String,
    default: '',
  },
  disable: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue', 'complete']);

const inputRefs = ref<HTMLInputElement[]>([]);
const otpValues = ref<string[]>(Array(props.length).fill(''));
const errors = ref<string[]>(Array(props.length).fill(''));

// Sync prop changes to internal state
watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal !== undefined) {
      const valueArray = String(newVal).split('').slice(0, props.length);
      otpValues.value = [...valueArray, ...Array(props.length - valueArray.length).fill('')];
    }
  },
  { immediate: true }
);

// Emit changes and completion
watch(
  otpValues,
  (newValues) => {
    const combinedValue = newValues.join('');
    emit('update:modelValue', combinedValue);
    if (combinedValue.length === props.length) {
      emit('complete', combinedValue);
    }
  },
  { deep: true }
);

const handleInput = (index: number) => {
  const value = otpValues.value[index];

  // Enforce Numeric Only
  if (value && !/^\d$/.test(value)) {
    otpValues.value[index] = '';
    return;
  }

  // Auto-advance focus
  if (value && index < props.length - 1) {
    inputRefs.value[index + 1]?.focus();
  }
};

const handleKeyDown = (event: KeyboardEvent, index: number) => {
  // Backspace logic: clear current and go back
  if (event.key === 'Backspace') {
    if (!otpValues.value[index] && index > 0) {
      otpValues.value[index - 1] = '';
      inputRefs.value[index - 1]?.focus();
      event.preventDefault();
    }
  }

  // Navigation logic
  if (event.key === 'ArrowLeft' && index > 0) {
    inputRefs.value[index - 1]?.focus();
    event.preventDefault();
  }

  if (event.key === 'ArrowRight' && index < props.length - 1) {
    inputRefs.value[index + 1]?.focus();
    event.preventDefault();
  }
};

const handlePaste = (event: ClipboardEvent) => {
  event.preventDefault();
  if (props.readonly || props.disable) return;

  const pastedData = event.clipboardData?.getData('text');
  if (!pastedData) return;

  const digits = pastedData.replace(/\D/g, '').split('').slice(0, props.length);

  digits.forEach((digit, index) => {
    if (index < props.length) {
      otpValues.value[index] = digit;
    }
  });

  // Focus the next empty slot or the last digit
  const nextEmptyIndex = otpValues.value.findIndex((val) => !val);
  const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : props.length - 1;
  inputRefs.value[focusIndex]?.focus();
};

onMounted(() => {
  if (inputRefs.value[0] && !props.disable && !props.readonly) {
    inputRefs.value[0].focus();
  }
});
</script>